import { useState } from 'react'
import { useFormContext } from 'react-hook-form'

import { FormTextFieldDS } from 'components/reusable/form/FormFields'
import { Button, ButtonGroup, CollapsibleContainer, useBreakpoint } from 'slab'
import { numberToOrdinalText } from 'utils/numberUtils'
import { type EmployersComplianceOwner } from 'utils/web_api.schemas'

interface OwnerEntryProps {
  index: number
  defaultOpen?: boolean
  onCreate: (ownerData: EmployersComplianceOwner) => Promise<void>
  onUpdate: (ownerId: string, ownerData: EmployersComplianceOwner) => Promise<void>
  onDelete: (ownerId: string, ownerIndex: number) => Promise<void>
  ownerId?: string
  isControllingOwner?: boolean
}

export function OwnerEntry ({ index, defaultOpen = true, onCreate, onUpdate, onDelete, ownerId, isControllingOwner = false }: OwnerEntryProps) {
  const { breakpointIsAtLeast } = useBreakpoint()
  const { register, getValues, trigger, setValue, formState: { errors, isSubmitting } } = useFormContext<{ owners: EmployersComplianceOwner[] }>()

  const [savedOwner, setSavedOwner] = useState<EmployersComplianceOwner | undefined>(getValues(`owners.${index}`)?.first_name ? getValues(`owners.${index}`) : undefined)
  const [isOpen, setIsOpen] = useState(defaultOpen)

  const onClickAddOwner = async () => {
    const ownerData = getValues(`owners.${index}`)

    // Ensure is_beneficial_owner is set to true for beneficial owners page
    const dataWithDefaults = {
      ...ownerData,
      is_beneficial_owner: true,
    }

    // Update the form with the default values
    setValue(`owners.${index}`, dataWithDefaults)

    const isValid = await trigger(`owners.${index}`)

    if (isValid) {
      try {
        if (ownerId) {
          // Update existing owner
          await onUpdate(ownerId, dataWithDefaults)
        } else {
          // Create new owner
          await onCreate(dataWithDefaults)
        }
        // Only mark as saved and close if the API call succeeds
        setSavedOwner(dataWithDefaults)
        setIsOpen(false)
      } catch (error) {
        // If there's an error, keep the form open and don't mark as saved
        console.error('Failed to save owner:', error)
      }
    }
  }

  const onClickRemoveOwner = async () => {
    try {
      await onDelete(ownerId || '', index)
    } catch (error) {
      console.error('Failed to delete owner:', error)
    }
  }

  function renderButtons () {
    const removeButton = (
      <Button
        variant='tertiary-destructive'
        onClick={onClickRemoveOwner}
        disabled={isSubmitting}
      >
        Remove owner
      </Button>
    )

    const addButton = (
      <Button
        variant='secondary'
        onClick={onClickAddOwner}
        disabled={isSubmitting}
      >
        {savedOwner ? 'Save changes' : 'Save owner'}
      </Button>
    )

    if (breakpointIsAtLeast('xs')) {
      return (
        <ButtonGroup>
          {isControllingOwner ? null : removeButton}
          {addButton}
        </ButtonGroup>
      )
    }

    return (
      <ButtonGroup layout='vertical'>
        {addButton}
        {removeButton}
      </ButtonGroup>
    )
  }

  function renderOwnerHeader () {
    if (savedOwner) {
      return (
        <div className='flex items-center gap-2'>
          <span className='font-medium'>{savedOwner.first_name} {savedOwner.last_name}</span>
        </div>
      )
    }
    return <span className='text-gray-500'>New owner</span>
  }

  return (
    <div className='flex flex-col gap-2'>
      <h2 className='body-sm-bold text-gray-800'>{numberToOrdinalText(index + 1)} owner</h2>
      <CollapsibleContainer
        heading={renderOwnerHeader()}
        open={isOpen}
        onOpenChange={setIsOpen}
      >
        <div className='flex flex-col flex-1 gap-6 p-4'>
          <div className='flex flex-row gap-4 flex-wrap'>
            <div className='flex-1 min-w-56'>
              <FormTextFieldDS<{ owners: EmployersComplianceOwner[] }>
                fieldId={`owners.${index}.first_name`}
                fieldLabel='Legal first name'
                register={register}
                placeholder='First name'
                validationRule={{
                  required: 'First name is required.',
                  pattern: {
                    value: /^[\p{L}\s'-]+$/u,
                    message: 'First name contains invalid characters'
                  }
                }}
                error={errors.owners?.[index]?.first_name}
                disabled={isSubmitting}
                autoFocus={isOpen}
              />
            </div>
            <div className='flex-1 min-w-56'>
              <FormTextFieldDS<{ owners: EmployersComplianceOwner[] }>
                fieldId={`owners.${index}.last_name`}
                fieldLabel='Legal last name'
                register={register}
                placeholder='Last name'
                validationRule={{
                  required: 'Last name is required.',
                  pattern: {
                    value: /^[\p{L}\s'-]+$/u,
                    message: 'Last name contains invalid characters'
                  }
                }}
                error={errors.owners?.[index]?.last_name}
                disabled={isSubmitting}
              />
            </div>
          </div>
          <div className='flex flex-row gap-4 flex-wrap'>
            <div className='flex-1 min-w-56'>
              <FormTextFieldDS<{ owners: EmployersComplianceOwner[] }>
                fieldId={`owners.${index}.title`}
                fieldLabel='Title'
                register={register}
                placeholder='CEO, Manager, Lead'
                validationRule={{ required: 'Title is required.' }}
                error={errors.owners?.[index]?.title}
                disabled={isSubmitting}
              />
            </div>
          </div>
          <div className='flex flex-row gap-4 flex-wrap'>
            <div className='flex-1 min-w-56'>
              <FormTextFieldDS<{ owners: EmployersComplianceOwner[] }>
                fieldId={`owners.${index}.email`}
                fieldLabel='Email'
                register={register}
                placeholder='person@example.com'
                validationRule={{
                  required: 'Email is required.',
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: 'Please enter a valid email address.'
                  }
                }}
                error={errors.owners?.[index]?.email}
                disabled={isSubmitting}
              />
              <p className='body-sm text-gray-500 mt-1'>
                We will send an email with instructions on how to complete identity verification to this email address.
              </p>
            </div>
          </div>
        </div>
        <div className='xs:flex xs:justify-end border-t border-gray-200 py-4 px-6'>
          {renderButtons()}
        </div>
      </CollapsibleContainer>
    </div>
  )
}
